import { css } from 'styled-components';

export const LIGHT_COLORS = {
    // Surfaces and controls
    pageBackground: '#ffffff',
    surface: '#ffffff',
    modalBackground: '#ffffff',
    modalControlBackground: '#FAFAFB',
    modalControlHover: '#E8E8ED',
    modalControlBorder: 'transparent',
    controlBackground: '#FAFAFB',
    controlHover: '#E8E8ED',
    controlBorder: 'transparent',
    secondaryButtonBackground: '#E8E8ED',
    secondaryButtonHover: '#FAFAFB',
    secondaryButtonText: '#6B6C83',
    secondaryGradientHoverBorder: '#BF63FF',
    disabledButtonBackground: '#E8E8ED',
    disabledButtonText: '#6B6C83',
    insetBackground: '#FAFAFB',
    sectionBackground: '#FAFAFB',
    landingCardBackground: '#FAFAFB',
    bannerBackground: '#FAFAFB',
    cardBorder: '#E8E8ED',
    amountPlaceholder: '#B3B4C3',
    modalCloseBackground: '#FAFAFB',
    modalCloseIcon: '#5C1283',
    inputBackground: '#ffffff',
    inputBorder: '#E8E8ED',
    inputActiveBorder: '#B3B4C3',
    raisedSurface: '#ffffff',
    profileBanner: '#24034F',
    tokenChipBackground: '#ffffff',
    tokenChipBorder: '#E8E8ED',
    roundedToggleBackground: '#ffffff',
    roundedToggleSelected: '#E8E8ED',
    toggleBackground: '#E8E8ED',
    toggleSelected: '#ffffff',
    toggleSelectedText: '#000636',
    toggleHover: '#ffffff',
    tableHover: '#FAFAFB',
    chartGrid: '#B3B4C3',
    apyBackground: '#B3B4C333',
    boostedApyBackground: '#1127E91A',
    logoBackground: 'transparent',
    iceBannerBackground: '#1127E9',
    iceBannerPattern: '#2A2ADE',
    diceVoteProgress: '#24034F',
    whitelistGlow: '#CCFADF',
    whitelistGlowFade: '#CCFADF',
    primaryButton: '#23024D',
    primaryButtonHover: '#872AB0',
    accentStrong: '#872AB0',
    dangerStrong: '#D92150',
    successStrong: '#00856D',
    infoStrong: '#1127E9',
    telegramBackground: '#3B82F6',
    discordBackground: '#5865F2',
    queueStrong: '#197983',
    discussionStrong: '#A85B00',
    mutedStrong: '#6B6C83',
    tooltip: '#8620B9',
    heroBackground: '#24034F',
    darkBanner: '#000636',
    overlay: '#0F0023',
    shadow: '#000636',
    purpleGlow: '#EABFFF',
    gradientStart: '#872AB0',
    gradientEnd: '#3918AC',
    gradientPink: '#BF61E8',
    gradientPurple: '#6423AF',
    gradientHover: '#AE51D6',
    gradientBlueHover: '#3B17B6',
    loaderHighlight: '#442868',
    skeletonBackground: '#E8E8ED',
    rangeTrack: '#D3B3E2',
    successBorder: '#9FEFC5',
    dangerBorder: '#FFB6C4',

    // Artwork on themed surfaces
    illustrationFill: '#ffffff',
    illustrationShade: '#E6E6EB',
    illustrationEdge: '#25134D',
    illustrationLine: '#23024D',
    illustrationDetail: '#000000',
    illustrationGuide: '#000636',
    illustrationAccent: '#5F0E8E',

    // Text colors
    textPrimary: '#23024D',
    textSecondary: '#000427',
    textTertiary: '#000636',
    textGray: '#6B6C83',
    textDescription: '#6B6C83',
    textDark: '#4B4E67',

    // Transparent and basic
    transparent: 'transparent',
    white: '#ffffff',
    black: '#000000',

    // Gray scale
    gray25: '#F7F7F7',
    gray10: '#F0F0F0',
    gray50: '#FAFAFB',
    gray100: '#E8E8ED',
    gray200: '#B3B4C3',
    gray300: '#9A9BAA',
    gray400: '#6B6C83',
    gray500: '#4B4E67',
    gray550: '#4D4F68',
    gray600: '#E9E6ED',

    // Purple scale
    purple50: '#F3EAF7',
    purple100: '#dab0ed',
    purple150: '#DAC0E9',
    purple200: '#BF63FF',
    purple300: '#4F345B',
    purple400: '#8620B9',
    purple500: '#872AB0',
    purple600: '#6D29B5',
    purple700: '#5C1283',
    purple800: '#461685',
    purple900: '#24034F',
    purple950: '#23024D',
    purple990: '#270158',

    // Blue scale
    blue300: '#2AA3B0',
    blue400: '#3B82F6',
    blue500: '#2E30F0',
    blue550: '#3918AC',
    blue600: '#5865F2',
    blue700: '#1127E9',

    // Cyan scale
    cyan50: '#E9F5F6',
    cyan500: '#24b1c7',

    // Green scale
    green50: '#CCFADF',
    green500: '#00B796',
    green900: '#002600',

    // Yellow scale
    yellow500: '#F8D26C',

    // Orange scale
    orange300: '#B0632A',
    orange500: '#FF8F00',

    // Red scale
    red50: '#FFE8ED',
    red500: '#FF3461',
    red700: '#AB0D0D',
};

/** Every UI color has a light and dark value; brand artwork stays independent. */
export const DARK_COLORS: typeof LIGHT_COLORS = {
    ...LIGHT_COLORS,
    // Surface hierarchy and control states from the Figma Elements palette.
    pageBackground: '#150131',
    surface: '#1D0041',
    modalBackground: '#1D0041',
    modalControlBackground: '#150131',
    modalControlHover: '#270158',
    modalControlBorder: '#270158',
    controlBackground: '#270158',
    controlHover: '#320271',
    controlBorder: '#320271',
    secondaryButtonBackground: '#320271',
    secondaryButtonHover: '#270158',
    secondaryButtonText: '#ffffff',
    secondaryGradientHoverBorder: '#A885C5',
    disabledButtonBackground: '#270158',
    disabledButtonText: '#FFFFFF4D',
    insetBackground: '#150131',
    sectionBackground: '#150131',
    landingCardBackground: '#1D0041',
    bannerBackground: '#24123D',
    cardBorder: '#FFFFFF0A',
    amountPlaceholder: '#ffffff',
    modalCloseBackground: '#1D0041',
    modalCloseIcon: '#ffffff',
    inputBackground: '#150131',
    inputBorder: '#170033',
    inputActiveBorder: '#320271',
    raisedSurface: '#270158',
    profileBanner: '#5C1283',
    tokenChipBackground: '#270158',
    tokenChipBorder: 'transparent',
    roundedToggleBackground: '#270158',
    roundedToggleSelected: '#B733F1',
    toggleBackground: '#270158',
    toggleSelected: '#B733F1',
    toggleSelectedText: '#ffffff',
    toggleHover: '#320271',
    tableHover: '#270158',
    chartGrid: '#270158',
    apyBackground: '#320271',
    boostedApyBackground: '#1127E94D',
    logoBackground: '#ffffff',
    iceBannerBackground: '#20123D',
    iceBannerPattern: '#2A2ADE',
    diceVoteProgress: '#A799F1',
    whitelistGlow: '#01866E',
    whitelistGlowFade: '#00B796',
    textPrimary: '#ffffff',
    textSecondary: '#ffffff',
    textTertiary: '#ffffff',
    textGray: '#8A8BA3',
    textDescription: '#FFFFFFB3',
    textDark: '#B3B4C3',
    gray25: '#1D0041',
    gray10: '#270158',
    gray50: '#150131',
    gray100: '#320271',
    gray200: '#6B6C83',
    gray300: '#B3B4C3',
    gray400: '#6B6C83',
    gray500: '#B3B4C3',
    gray550: '#8A8BA3',
    gray600: '#320271',
    purple50: '#270158',
    purple100: '#320271',
    purple150: '#5A1197',
    purple200: '#B733F1',
    purple300: '#B3B4C3',
    purple400: '#B733F1',
    purple500: '#B733F1',
    purple600: '#B733F1',
    purple700: '#B733F1',
    purple800: '#B733F1',
    purple900: '#ffffff',
    purple950: '#ffffff',
    purple990: '#ffffff',
    blue300: '#64C7D1',
    blue400: '#82ACFF',
    blue500: '#A5A3FF',
    blue550: '#A799F1',
    blue600: '#9CA5FF',
    blue700: '#A1ACFF',
    cyan50: '#173138',
    cyan500: '#61C9D9',
    green50: '#CCFADF',
    green500: '#54D5AC',
    green900: '#002600',
    yellow500: '#665017',
    orange300: '#E9A978',
    orange500: '#FFB75E',
    red50: '#FFE8ED',
    red500: '#FF3461',
    red700: '#AB0D0D',
    primaryButton: '#B733F1',
    primaryButtonHover: '#8620B9',
    accentStrong: '#B733F1',
    dangerStrong: '#B52D51',
    successStrong: '#18745C',
    infoStrong: '#2E31F0',
    queueStrong: '#226974',
    discussionStrong: '#925007',
    mutedStrong: '#675A7C',
    tooltip: '#270158',
    overlay: '#0C001C',
    shadow: '#09031A',
    skeletonBackground: '#3B2854',
    rangeTrack: '#5A1197',
    successBorder: '#2B765C',
    dangerBorder: '#7E3B50',
    illustrationFill: '#320271',
    illustrationShade: '#5A1197',
    illustrationEdge: '#A274CF',
    illustrationLine: '#B8A1D4',
    illustrationDetail: '#C6BCD9',
    illustrationGuide: '#77658F',
    illustrationAccent: '#A274CF',
};

export type ThemeColors = typeof LIGHT_COLORS;

// CSS variables also work in portals, SVG attributes and lazily loaded styles.
// Canvas consumers use the resolved palette returned by useTheme instead.
export const COLORS = Object.fromEntries(
    Object.entries(LIGHT_COLORS).map(([name, value]) => [name, `var(--color-${name}, ${value})`]),
) as ThemeColors;

/**
 * Adds opacity to a HEX color or a theme CSS variable.
 * Supports: "#RGB", "RGB", "#RRGGBB", "RRGGBB", "#RRGGBBAA".
 */
export const hexWithOpacity = (hex: string, opacityPercent: number): string => {
    if (!hex) {
        throw new Error('hex is required');
    }

    if (hex.startsWith('var(')) {
        const opacity = Number(opacityPercent);
        if (Number.isNaN(opacity)) {
            throw new Error('opacityPercent must be a number');
        }
        return `color-mix(in srgb, ${hex} ${Math.max(0, Math.min(100, opacity))}%, transparent)`;
    }

    const raw = hex.trim().replace(/^#/, '');
    let rgb: string;

    if (raw.length === 3) {
        // expand shorthand #RGB to #RRGGBB
        rgb = raw
            .split('')
            .map(ch => ch + ch)
            .join('');
    } else if (raw.length === 6) {
        rgb = raw;
    } else if (raw.length === 8) {
        // drop existing alpha, replace with new one
        rgb = raw.slice(0, 6);
    } else {
        throw new Error('Unsupported HEX format. Use #RGB, #RRGGBB or #RRGGBBAA.');
    }

    let pct = Number(opacityPercent);
    if (Number.isNaN(pct)) {
        throw new Error('opacityPercent must be a number');
    }

    // clamp between 0 and 100
    pct = Math.max(0, Math.min(100, pct));

    // convert percentage to 0..255 alpha
    const alpha = Math.round((pct * 255) / 100);

    // convert to 2-digit HEX
    const aa = alpha.toString(16).padStart(2, '0').toUpperCase();

    return `#${rgb.toUpperCase()}${aa}`;
};

export const FONT_FAMILY = {
    roboto: 'Roboto, sans-serif',
};

export const FONT_SIZE = {
    xxs: css`
        font-size: 1rem;
        line-height: 2rem;
    `,
    xs: css`
        font-size: 1.2rem;
        line-height: 2rem;
    `,
    sm: css`
        font-size: 1.4rem;
        line-height: 2rem;
    `,
    md: css`
        font-size: 1.6rem;
        line-height: 2.8rem;
    `,
    lg: css`
        font-size: 2rem;
        line-height: 2.2rem;
    `,
    xl: css`
        font-size: 3rem;
        line-height: 3.6rem;
    `,
    xxl: css`
        font-size: 5.6rem;
        line-height: 6.4rem;
    `,
};

export const Z_INDEX = {
    header: 30,
    accountMenu: 450,
    tooltip: 400,
    modal: 500,
    toast: 600,
};

export enum Breakpoints {
    xs = '480px',
    sm = '768px',
    md = '992px',
    lg = '1200px',
    xl = '1400px',
    xxl = '1600px',
    xxxl = '1800px',
}

export const MAX_WIDTHS = {
    common: '122rem',
    wide: '136rem',
};

// in rem
export const PAGE_PADDINGS = 2.4;
export const HEADER_HEIGHT = '11.2rem';
export const HEADER_HEIGHT_MOBILE = '5.8rem';
